<template>
  <div class="modal-overlay" @click.self="$emit('close')" data-testid="wallet-modal">
    <div class="wallet-modal">
      <!-- Left: Wallet Selection -->
      <div class="wallet-panel" data-testid="wallet-panel">
        <div class="wallet-panel__header">
          <h2 class="wallet-panel__title">
            Add your wallet address
            <span class="info-icon">?</span>
          </h2>
          <p class="wallet-panel__desc">
            Your tickets will be issued as digital assets. Select a saved wallet or add a new one to receive them directly.<br /><br />
            Tickets will be minted to your selected wallet after checkout.
          </p>
        </div>

        <div class="wallet-list">
          <button
            v-for="w in wallets"
            :key="w.address"
            class="wallet-pill"
            :class="{ 'wallet-pill--selected': selectedWallet === w.address }"
            @click="selectedWallet = w.address"
            data-testid="wallet-option"
          >
            <span class="wallet-pill__dot" :class="{ 'wallet-pill__dot--on': selectedWallet === w.address }"></span>
            <span class="wallet-icon">&#9673;</span>
            <span>{{ w.label }} - {{ shortenAddress(w.address) }}</span>
          </button>

          <button class="wallet-pill wallet-pill--add" @click="addWallet" data-testid="add-wallet-btn">
            Add wallet address
          </button>

          <label class="gift-row">
            <input type="checkbox" v-model="giftTickets" class="gift-row__check" data-testid="gift-checkbox" />
            <span>Gift these tickets to someone else</span>
          </label>
        </div>

        <div class="wallet-footer">
          <button
            class="btn-dark"
            :disabled="!selectedWallet"
            @click="handleConfirm"
            data-testid="wallet-continue-btn"
          >Continue</button>
        </div>
      </div>

      <!-- Right: Order Summary -->
      <div class="order-summary" data-testid="order-summary">
        <h2 class="order-summary__title">Order summary</h2>
        <div class="order-summary__items">
          <div
            v-for="item in cartItems"
            :key="item.tierId"
            class="summary-row"
          >
            <span>{{ item.quantity }} x {{ item.name }}</span>
            <span>${{ (item.quantity * parseFloat(item.price || 0)).toFixed(2) }}</span>
          </div>
          <hr class="summary-divider" />
          <div class="summary-row">
            <span>Subtotal</span>
            <span>${{ totalPrice.toFixed(2) }}</span>
          </div>
          <div class="summary-row">
            <span>Fees</span>
            <span>$0.00</span>
          </div>
          <hr class="summary-divider" />
          <div class="summary-row summary-row--total">
            <span>Total</span>
            <span>${{ totalPrice.toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WalletModal',
  emits: ['close', 'confirm'],
  props: {
    cartItems: { type: Array, default: () => [] },
    totalPrice: { type: Number, default: 0 },
    event: { type: Object, default: null },
  },
  data() {
    return {
      selectedWallet: '',
      giftTickets: false,
      wallets: [
        { label: 'Metamask', address: '0xAbC1344CdE56789012345678901234567890abcd' },
      ],
    }
  },
  methods: {
    shortenAddress(addr) {
      if (!addr) return ''
      return addr.slice(0, 10) + '...' + addr.slice(-3)
    },
    addWallet() {
      const addr = prompt('Enter wallet address:')
      if (addr && addr.startsWith('0x')) {
        this.wallets.push({ label: 'Custom', address: addr })
        this.selectedWallet = addr
      }
    },
    handleConfirm() {
      if (!this.selectedWallet) return
      this.$emit('confirm', this.selectedWallet)
    },
  },
}
</script>

<style scoped lang="scss">
$text: #0a0a0a;
$dark-btn: rgba(10, 10, 10, 0.8);
$border-muted: rgba(0, 0, 0, 0.1);
$radius-lg: 36px;
$radius-pill: 44px;
$font: 'Inter', sans-serif;

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.wallet-modal {
  background: #f8f8fa;
  border-radius: $radius-lg;
  display: flex;
  gap: 18px;
  padding: 32px;
  width: 1100px;
  max-width: 95vw;
  max-height: 90vh;
  overflow: hidden;
  font-family: $font;
}

/* ── Wallet Panel ── */
.wallet-panel {
  background: #fff;
  border-radius: $radius-lg;
  width: 600px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &__header { padding: 24px 24px 0; }
  &__title {
    font-size: 20px;
    font-weight: 800;
    margin: 0 0 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__desc {
    font-size: 16px;
    font-weight: 400;
    opacity: 0.8;
    line-height: 1.5;
    margin: 0;
  }
}

.info-icon {
  width: 20px; height: 20px;
  background: #eee;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
}

.wallet-list {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow-y: auto;
}

.wallet-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 48px;
  border-radius: $radius-pill;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.3);
  color: $text;
  text-align: left;
  font-family: $font;
  transition: border-color 0.15s;

  &--selected {
    border: 2px solid $text;
  }
  &--add {
    border: 1px solid rgba(0, 0, 0, 0.3);
    font-weight: 700;
  }
  &__dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: transparent;
    flex-shrink: 0;
    &--on { background: $text; }
  }
}

.wallet-icon { font-size: 18px; }

.gift-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 16px;
  cursor: pointer;
  &__check { width: 20px; height: 20px; cursor: pointer; }
}

.wallet-footer {
  padding: 24px;
  border-top: 1px solid $border-muted;
  display: flex;
  justify-content: flex-end;
}

/* ── Order Summary ── */
.order-summary {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: $radius-lg;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;

  &__title { font-size: 20px; font-weight: 800; margin: 0; }
  &__items { display: flex; flex-direction: column; gap: 12px; }
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: 400;
  color: $text;
  &--total { font-size: 20px; font-weight: 700; }
}

.summary-divider {
  border: none;
  border-top: 1px solid $border-muted;
  margin: 0;
}

/* ── Shared ── */
.btn-dark {
  background: $dark-btn;
  color: #fff;
  border: none;
  border-radius: $radius-pill;
  padding: 20px 24px;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  font-family: $font;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}
</style>
